import React, { useEffect, useRef, useState } from 'react';
import { Item } from '@/stores/canvasStore';
import { ImageLightbox } from '@/components/modals/ImageLightbox';
import { ImageIcon } from 'lucide-react';
import { LoadingImage } from '@/components/ui/loading-image';

interface ImageCardProps {
  item: Item;
  /** Bumped by the canvas on a double-click: the press is a canvas drag, so no dblclick reaches the card. */
  openRequest?: number;
}

export function ImageCard({ item, openRequest = 0 }: ImageCardProps) {
  const content = item.content as { url?: string; alt?: string; mimeType?: string };
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Only a request made after mount opens it, so a remount doesn't reopen the lightbox
  const seenOpenRequest = useRef(openRequest);
  useEffect(() => {
    if (openRequest === seenOpenRequest.current) return;
    seenOpenRequest.current = openRequest;
    if (content.url) setIsLightboxOpen(true);
  }, [openRequest, content.url]);

  // Check if this is a GIF
  const isGif = content.mimeType === 'image/gif' || 
    content.url?.toLowerCase().endsWith('.gif') || 
    content.alt?.toLowerCase().endsWith('.gif');

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (content.url) {
      setIsLightboxOpen(true);
    }
  };

  if (!content.url || hasError) {
    return (
      <div className="w-full h-full bg-muted rounded-lg card-shadow flex items-center justify-center border border-border">
        <ImageIcon className="w-12 h-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <div
        className="w-full h-full rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow relative"
        onDoubleClick={handleDoubleClick}
      >
        {isGif && (
          <span className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide z-10">
            GIF
          </span>
        )}
        <LoadingImage
          src={content.url}
          alt={content.alt || 'Image'}
          wrapperClassName="w-full h-full rounded-lg"
          className="w-full h-full object-cover rounded-lg"
          onError={() => setHasError(true)}
          draggable={false}
        />
      </div>
      
      <ImageLightbox
        url={content.url}
        alt={content.alt}
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
      />
    </>
  );
}
